import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Send as SendIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { BriefData } from '../types/briefTypes';
import { extractInformation, generateTargetAudience } from '../services/openai';
import { SpeechRecognition } from '../types/speechTypes';
import { ConversationConfig } from '../types/conversationTypes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 2rem;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 80%;
  padding: 1rem;
  border-radius: 0.75rem;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  background-color: ${props => props.isUser ? '#2563eb' : '#f3f4f6'};
  color: ${props => props.isUser ? '#ffffff' : '#111827'};
  font-size: 0.875rem;
  line-height: 1.5;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);

  & p {
    margin: 0;
    white-space: pre-line;
  }

  & strong {
    font-weight: 600;
    color: inherit;
  }

  & ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  & li {
    margin: 0.25rem 0;
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
`;

const StyledInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  background: transparent;
  color: #111827;
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const StyledIconButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 0.5rem;
  background-color: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  color: ${props => props.active ? '#ffffff' : '#6b7280'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? '#1d4ed8' : '#e5e7eb'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
`;

const VoiceToggle = styled(FormControlLabel)`
  margin: 0 !important;
  .MuiTypography-root {
    color: #6b7280;
    font-size: 0.875rem;
  }
  .MuiSwitch-track {
    background-color: #e5e7eb !important;
  }
  .Mui-checked + .MuiSwitch-track {
    background-color: #2563eb !important;
    opacity: 0.5 !important;
  }
  .MuiSwitch-thumb {
    background-color: #ffffff;
  }
  .Mui-checked .MuiSwitch-thumb {
    background-color: #2563eb !important;
  }
  .MuiSwitch-root {
    padding: 8px !important;
  }
  svg {
    font-size: 1.25rem;
    color: #6b7280;
  }
`;

interface Message {
  text: string;
  isUser: boolean;
}

interface Props {
  onUpdateBrief: (data: Partial<BriefData>) => void;
  briefData: BriefData;
  conversationConfig: ConversationConfig;
}

const ChatInterface: React.FC<Props> = ({ onUpdateBrief, briefData, conversationConfig }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      text: conversationConfig.initialGreeting,
      isUser: false,
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthesis = window.speechSynthesis;
  const currentUtterance = useRef<SpeechSynthesisUtterance | null>(null);
  const [autoSend, setAutoSend] = useState(true);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const SILENCE_TIMEOUT = 2000;
  const availableVoicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const messageQueue = useRef<Message[]>([]);
  const isProcessingQueue = useRef(false);
  const latestTranscriptRef = useRef<string>('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const speakMessage = useCallback(async (text: string) => {
    if (!isVoiceEnabled || !text) return;

    return new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pl-PL';

      const polishVoice = availableVoicesRef.current.find(
        voice => voice.lang.startsWith('pl')
      );
      if (polishVoice) {
        utterance.voice = polishVoice;
      }

      utterance.onstart = () => {
        setIsSpeaking(true);
        currentUtterance.current = utterance;
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        currentUtterance.current = null;
        resolve();
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
        currentUtterance.current = null;
        resolve();
      };

      speechSynthesis.speak(utterance);
    });
  }, [isVoiceEnabled, speechSynthesis]);

  const processMessageQueue = useCallback(async () => {
    if (messageQueue.current.length === 0) {
      isProcessingQueue.current = false;
      return;
    }

    isProcessingQueue.current = true;
    const message = messageQueue.current[0];

    if (!message.isUser && isVoiceEnabled) {
      await speakMessage(message.text);
    }

    messageQueue.current.shift();
    processMessageQueue();
  }, [isVoiceEnabled, speakMessage]);

  const addMessage = useCallback(async (message: Message) => {
    setMessages(prev => [...prev, message]);
    messageQueue.current.push(message);
    if (!isProcessingQueue.current) {
      processMessageQueue();
    }
  }, [processMessageQueue]);

  const showPrerequisites = useCallback(async (campaignType: string) => {
    const prerequisites = conversationConfig.prerequisites.find(
      p => p.campaignType.toLowerCase() === campaignType.toLowerCase()
    );

    if (prerequisites && prerequisites.items.length > 0) {
      const prerequisiteMessage = `Lista rzeczy dla ${campaignType}:\n\n${prerequisites.items
        .map(item => `${item.required ? '❗' : '•'} ${item.text}`)
        .join('\n')}`;

      await addMessage({ text: prerequisiteMessage, isUser: false });
    }
  }, [conversationConfig.prerequisites, addMessage]);

  const isTargetAudienceRequest = (text: string): boolean => {
    const lowerText = text.toLowerCase();
    const patterns = [
      'zaproponuj grupę',
      'grupa docelowa',
      'grupa odbiorców',
      'jaka grupa',
      'jacy odbiorcy',
      'jaka może być grupa',
      'odbiorcy',
      'odbiorcą',
      'odbiorca',
      'docelowym',
      'grupą docelową',
      'target',
      'audience',
      'zaproponować grupę'
    ];
    return patterns.some(pattern => lowerText.includes(pattern));
  };

  const cleanTargetAudienceText = (text: string): string => {
    // Remove the intro line and any leading/trailing whitespace
    return text
      .replace(/^Oto propozycje targetowania dla różnych platform reklamowych:\s*\n+/, '')
      .trim();
  };

  const handleSend = useCallback(async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, isUser: true };
    await addMessage(userMessage);
    setInput('');
    setIsLoading(true);

    try {
      // First check if this is a target audience request
      if (isTargetAudienceRequest(input)) {
        console.log('Target audience request detected, generating suggestion...');
        const targetAudienceSuggestion = await generateTargetAudience(briefData);
        if (targetAudienceSuggestion) {
          // Save the cleaned suggestion to the brief
          onUpdateBrief({
            targetAudience: cleanTargetAudienceText(targetAudienceSuggestion)
          });

          // Show the full suggestion to the user
          await addMessage({ 
            text: targetAudienceSuggestion,
            isUser: false 
          });

          // Confirm that it's been saved
          await addMessage({
            text: "Zapisałem te sugestie grup docelowych w briefie.",
            isUser: false
          });

          setIsLoading(false);
          return;
        }
      }

      // If not a target audience request, proceed with information extraction
      const extractedInfo = await extractInformation(input) as Partial<BriefData>;
      console.log('Extracted info:', extractedInfo);
      
      if (Object.keys(extractedInfo).length === 0) {
        await addMessage({ 
          text: "Przepraszam, nie udało mi się wydobyć żadnych informacji z tej wiadomości. Czy możesz powiedzieć to inaczej?",
          isUser: false 
        });
      } else {
        // Process the extracted information
        const processedInfo = Object.entries(extractedInfo).reduce<Partial<BriefData>>((acc, [key, value]) => {
          if (value === null || value === undefined) return acc;
          
          const currentValue = briefData[key as keyof BriefData];
          
          // Special handling for campaignType
          if (key === 'campaignType' && Array.isArray(value)) {
            acc[key] = value.join(' i ');
          }
          // Handle arrays that should be strings
          else if (Array.isArray(value) && typeof currentValue === 'string') {
            acc[key as keyof BriefData] = value.join(', ');
          }
          // Handle arrays that should remain arrays
          else if (Array.isArray(value) && Array.isArray(currentValue)) {
            acc[key as keyof BriefData] = value;
          }
          // Handle simple values
          else {
            acc[key as keyof BriefData] = value;
          }
          return acc;
        }, {});

        console.log('Processed info:', processedInfo);
        onUpdateBrief(processedInfo);

        // Handle campaign type and name for acknowledgment
        const campaignType = processedInfo.campaignType || briefData.campaignType;
        const campaignName = processedInfo.campaignName || briefData.campaignName;
        const eventDate = processedInfo.eventDate || briefData.eventDate;
        
        let acknowledgment = "Zapisałem podane informacje w briefie.";
        
        if (campaignType && campaignName) {
          acknowledgment = `Świetnie! Planujesz ${campaignType.toLowerCase()} pod nazwą "${campaignName}"${eventDate ? ` zaplanowany na ${eventDate}` : ''}. Dodałem te informacje do briefu.`;
          
          // Show prerequisites if campaign type was just set or changed
          if (campaignType !== briefData.campaignType) {
            await addMessage({ text: acknowledgment, isUser: false });
            await showPrerequisites(campaignType.split(' i ')[0]);
            return;
          }
        } else if (campaignType) {
          acknowledgment = `Doskonale! ${campaignType} to dobry wybór. Zaktualizowałem brief o te informacje.`;
          
          // Show prerequisites if campaign type was just set
          if (campaignType !== briefData.campaignType) {
            await addMessage({ text: acknowledgment, isUser: false });
            await showPrerequisites(campaignType.split(' i ')[0]);
            return;
          }
        }
        
        await addMessage({ text: acknowledgment, isUser: false });
      }
    } catch (error) {
      console.error('Error processing message:', error);
      await addMessage({ 
        text: "Przepraszam, wystąpił błąd podczas przetwarzania wiadomości. Spróbuj ponownie.",
        isUser: false 
      });
    } finally {
      setIsLoading(false);
    }
  }, [input, addMessage, briefData, onUpdateBrief, showPrerequisites]);

  useEffect(() => {
    if (autoSend && input === latestTranscriptRef.current && input.trim()) {
      handleSend();
      latestTranscriptRef.current = '';
    }
  }, [input, autoSend, handleSend]);

  useEffect(() => {
    try {
      if (!('webkitSpeechRecognition' in window)) {
        console.warn('Speech recognition is not supported in this browser');
        return;
      }

      const SpeechRecognition = window.webkitSpeechRecognition;
      const newRecognition = new SpeechRecognition();

      newRecognition.continuous = true;
      newRecognition.interimResults = true;
      newRecognition.lang = 'pl-PL';

      newRecognition.onstart = () => {
        console.log('Speech recognition started');
        setIsRecording(true);
        setInput('');
      };

      newRecognition.onresult = (event) => {
        if (isSpeaking) return;
        
        const lastResult = event.results[event.results.length - 1];
        const transcript = lastResult[0].transcript.trim();
        console.log('Transcript:', transcript);
        
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }

        setInput(transcript);

        if (lastResult.isFinal) {
          console.log('Final transcript:', transcript);
          latestTranscriptRef.current = transcript;

          silenceTimeoutRef.current = setTimeout(() => {
            if (isRecording) {
              console.log('Stopping recording due to silence');
              newRecognition.stop();
            }
          }, SILENCE_TIMEOUT);
        }
      };

      newRecognition.onerror = (event) => {
        console.error('Speech recognition error:', event);
        setIsRecording(false);
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
      };

      newRecognition.onend = () => {
        console.log('Speech recognition ended');
        setIsRecording(false);
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
      };

      setRecognition(newRecognition);
      console.log('Speech recognition initialized successfully');
    } catch (error) {
      console.error('Error initializing speech recognition:', error);
    }
  }, [isRecording, isSpeaking]);

  const toggleRecording = useCallback(() => {
    if (!recognition) {
      console.warn('Speech recognition not available');
      return;
    }

    if (isSpeaking) {
      console.log('Cannot start recording while speaking');
      return;
    }

    try {
      if (isRecording) {
        console.log('Stopping recording...');
        recognition.stop();
      } else {
        console.log('Starting recording...');
        setInput('');
        recognition.start();
      }
    } catch (error) {
      console.error('Error toggling recording:', error);
      setIsRecording(false);
    }
  }, [recognition, isSpeaking, isRecording, setInput, setIsRecording]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const stopSpeaking = () => {
    if (isSpeaking && currentUtterance.current) {
      speechSynthesis.cancel();
      currentUtterance.current = null;
      setIsSpeaking(false);
    }
  };

  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      if (voices.length > 0) {
        availableVoicesRef.current = voices;
      }
    };

    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      speechSynthesis.onvoiceschanged = null;
    };
  }, [speechSynthesis]);

  useEffect(() => {
    return () => {
      if (currentUtterance.current) {
        speechSynthesis.cancel();
      }
    };
  }, [speechSynthesis]);

  return (
    <ChatContainer>
      <MessagesContainer>
        {messages.map((message, index) => (
          <MessageBubble key={index} isUser={message.isUser}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.text}
            </ReactMarkdown>
          </MessageBubble>
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <ControlsContainer>
        <VoiceToggle
          control={
            <Switch
              checked={isVoiceEnabled}
              onChange={(e) => {
                setIsVoiceEnabled(e.target.checked);
                if (!e.target.checked) {
                  stopSpeaking();
                }
              }}
              size="small"
            />
          }
          label={isVoiceEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
        />
        <VoiceToggle
          control={
            <Switch
              checked={autoSend}
              onChange={(e) => setAutoSend(e.target.checked)}
              size="small"
            />
          }
          label={<AutorenewIcon />}
          title="Auto-send voice messages"
        />
      </ControlsContainer>

      <InputContainer>
        <StyledInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isRecording ? "Listening..." : "Type your message..."}
          disabled={isLoading}
        />
        <StyledIconButton
          onClick={toggleRecording}
          active={isRecording}
          disabled={isLoading}
        >
          {isRecording ? <StopIcon /> : <MicIcon />}
        </StyledIconButton>
        <StyledIconButton
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          active={!isLoading && !!input.trim()}
        >
          {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
        </StyledIconButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default ChatInterface; 